import { I18nManager, Pressable, StyleSheet } from 'react-native';
import { ChevronLeft } from 'lucide-react-native';
import { useTheme } from '@/hooks/useTheme';

interface LeadingTitleHeaderArgs {
  title: string;
  /** Fired when the leading chevron is pressed (open drawer / go back / etc.). */
  onLeadingPress: () => void;
  leadingAccessibilityLabel?: string;
}

/**
 * Shared native-stack header used across the main screens: a bare leading chevron
 * (`<`) on the start side with the title centered.
 *
 * The chevron is rendered in `headerLeft` (not the native back button) so it stays a
 * plain `<` with no back-label text and keeps our custom press behaviour + haptics.
 * The title is left to the native centered slot via `title`; `headerTitleAlign:
 * 'center'` makes Android center it too (iOS centers by default).
 *
 * Returns `Stack.Screen` options; spread into the screen's `options` and override
 * extras (e.g. `headerRight`) afterwards as needed.
 */
export function useLeadingTitleHeader({
  title,
  onLeadingPress,
  leadingAccessibilityLabel,
}: LeadingTitleHeaderArgs) {
  const { theme } = useTheme();

  return {
    title,
    headerLargeTitle: false,
    headerTransparent: false,
    headerTintColor: theme.text,
    headerStyle: { backgroundColor: theme.background },
    headerShadowVisible: false,
    headerTitleAlign: 'center',
    headerLeft: () => (
      <Pressable
        onPress={onLeadingPress}
        hitSlop={12}
        accessibilityRole="button"
        accessibilityLabel={leadingAccessibilityLabel}
        style={({ pressed }) => [styles.button, { opacity: pressed ? 0.5 : 1 }]}
      >
        <ChevronLeft
          size={26}
          color={theme.text}
          strokeWidth={2}
          style={I18nManager.isRTL ? styles.flip : undefined}
        />
      </Pressable>
    ),
  } as const;
}

const styles = StyleSheet.create({
  button: { padding: 4 },
  flip: { transform: [{ rotate: '180deg' }] },
});
